/**
 * Context Provider para configurações de UI
 * Fornece acesso global às configurações vindas da API
 */

import { createContext, useContext, ReactNode } from 'react'
import { useUIConfig } from '../hooks/useUIConfig'
import type { UIConfig } from '../types/settings'

interface UIConfigContextValue {
  uiConfig: UIConfig | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  clearCache: () => void
}

const UIConfigContext = createContext<UIConfigContextValue | null>(null)

interface UIConfigProviderProps {
  children: ReactNode
  tennant: string
  player?: string
  apiBaseUrl?: string
}

/**
 * Provider que carrega e fornece configurações de UI
 */
export function UIConfigProvider({ 
  children, 
  tennant, 
  player,
  apiBaseUrl 
}: UIConfigProviderProps) {
  const { data, isLoading, error, refetch, clearCache } = useUIConfig(tennant, player, apiBaseUrl)
  
  const value: UIConfigContextValue = {
    uiConfig: data,
    isLoading,
    error,
    refetch,
    clearCache
  }

  return (
    <UIConfigContext.Provider value={value}>
      {children}
    </UIConfigContext.Provider>
  )
}

/**
 * Hook para acessar configurações de UI do contexto
 */
export function useUIConfigContext(): UIConfigContextValue {
  const context = useContext(UIConfigContext)
  
  if (!context) {
    throw new Error('useUIConfigContext deve ser usado dentro de UIConfigProvider')
  }
  
  return context
}

/**
 * Hook para acessar apenas as cores de categoria
 */
export function useCategoryColorsFromContext() {
  const { uiConfig } = useUIConfigContext()
  return uiConfig?.categoryColors ?? []
}

/**
 * Hook para acessar apenas as cores de pastas
 */
export function useFolderColorsFromContext() {
  const { uiConfig } = useUIConfigContext()
  return uiConfig?.folderColors ?? []
}

/**
 * Hook para acessar apenas as cores de anotações
 */
export function useAnnotationColorsFromContext() {
  const { uiConfig } = useUIConfigContext()
  return uiConfig?.annotationColors ?? []
}

/**
 * Hook para acessar apenas as categorias de anotações
 */
export function useAnnotationCategoriesFromContext() {
  const { uiConfig } = useUIConfigContext()
  return uiConfig?.annotationCategories ?? []
}

/**
 * Hook para acessar extensões e MIME types
 */
export function useMediaExtensionsFromContext() {
  const { uiConfig } = useUIConfigContext()
  return {
    audioExtensions: uiConfig?.audioExtensions ?? [],
    videoExtensions: uiConfig?.videoExtensions ?? [],
    audioMimeTypes: uiConfig?.audioMimeTypes ?? [],
    videoMimeTypes: uiConfig?.videoMimeTypes ?? []
  }
}
